import React, { useState, useCallback, useMemo } from 'react';
import { Typography, List } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { 
  StyledDrawer, 
  DrawerContent, 
  MenuHeader,
  scrollbarStyles 
} from '../shared/StyledComponents';
import { FANTASY_COLORS, STATUS_ICONS } from '../../constants/gameConstants';

const InitiativeList = styled(List)(({ theme }) => ({
  '& .over': {
    borderTop: `2px solid ${theme.palette.background.main}`,
  },
  padding: theme.spacing(1),
  maxHeight: 'calc(100vh - 180px)',
  overflow: 'auto',
  ...scrollbarStyles(theme)
}));

const InitiativeItem = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.main,
  border: `1px solid ${theme.palette.background.main}`,
  borderRadius: theme.shape.borderRadius,
  cursor: 'grab',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '4px',
    backgroundColor: color || theme.palette.background.main,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:active': {
    cursor: 'grabbing',
  }
}));

const TokenColorDot = styled(Box)(({ color }) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  backgroundColor: color || '#666',
  border: '1px solid rgba(0,0,0,0.2)',
  marginRight: 8,
}));

const InitiativeDrawer = ({
  isOpen,
  onClose,
  initiativeOrder,
  onInitiativeUpdate,
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // All coins are now on the main grid
  const filteredInitiativeOrder = useMemo(() => {
    return initiativeOrder.filter(coin => coin.y !== undefined);
  }, [initiativeOrder]);

  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    const fromIndex = draggedIndex;
    if (fromIndex !== null && fromIndex !== dropIndex) {
      // Reorder within the filtered list
      const newFilteredOrder = [...filteredInitiativeOrder];
      const [draggedItem] = newFilteredOrder.splice(fromIndex, 1);
      newFilteredOrder.splice(dropIndex, 0, draggedItem);
      
      // Rebuild the full initiative order with reordered coins
      const coinsWithoutPosition = initiativeOrder.filter(coin => coin.y === undefined);
      const newFullOrder = [...coinsWithoutPosition, ...newFilteredOrder];
      
      onInitiativeUpdate(newFullOrder);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, filteredInitiativeOrder, initiativeOrder, onInitiativeUpdate]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const getColorName = (colorHex) => {
    const colorObj = FANTASY_COLORS.find(c => c.color.toLowerCase() === colorHex.toLowerCase());
    return colorObj ? colorObj.name : 'Unknown';
  };

  return (
    <StyledDrawer anchor="left" open={isOpen} onClose={onClose}>
      <DrawerContent>
        <MenuHeader>
          <Typography>Turn Order</Typography>
        </MenuHeader>

        <InitiativeList>
          {filteredInitiativeOrder.map((coin, index) => (
            <InitiativeItem
              key={coin.id}
              color={coin.color}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                opacity: draggedIndex === index ? 0.5 : 1,
                borderTop: dragOverIndex === index && draggedIndex !== null && draggedIndex > index ? '2px solid #FFD700' : undefined,
                borderBottom: dragOverIndex === index && draggedIndex !== null && draggedIndex < index ? '2px solid #FFD700' : undefined,
              }}
            >
              <Typography 
                sx={{ 
                  minWidth: 28, 
                  fontWeight: 'bold', 
                  color: 'text.secondary',
                  fontFamily: 'MedievalSharp',
                }}
              >
                {index + 1}.
              </Typography>
              <TokenColorDot color={coin.color} />
              <Typography sx={{ flexGrow: 1 }}>
                {coin.name || getColorName(coin.color)}
              </Typography>

              {/* Status Icons */}
              {coin.statuses && coin.statuses.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
                  {coin.statuses.map((status, idx) => (
                    STATUS_ICONS[status] && (
                      <Box
                        key={idx}
                        component="img"
                        src={STATUS_ICONS[status]}
                        alt={status}
                        title={status}
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                        }}
                      />
                    )
                  ))}
                </Box>
              )}

              { coin.isEnemy && <Typography sx={{ color: 'red' }}>Enemy</Typography>}
            </InitiativeItem>
          ))}
        </InitiativeList>
      </DrawerContent>
    </StyledDrawer>
  );
};

export default InitiativeDrawer;

